import { FC, useState } from 'react';
import {
  Button,
  Radio,
  RadioGroup,
  // Radio,
  // RadioGroup,
  Spacer,
  TextSubtitle,
  TextTitle,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  TranslationKeys,
  useTranslation,
} from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '..';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../../xstate/app.xstate';
import { SolutionVersion } from '../../../xstate/prostudio.api-service.types';
import { capitalizeWords } from '../../../utils';
import { sendUserMessage } from '../../../xstate/app.xstate-utils';

export type RepublishModalProps = GenericModalProps & {
  botVersion?: SolutionVersion;
};

// const radioOptions = [
//   {
//     label: 'Republish all files',
//     value: '1',
//   },
//   {
//     label: 'Republish without Actions',
//     value: '2',
//   },
// ];

export const RepublishModal: FC<RepublishModalProps> = ({
  botVersion,
  modalRef,
  onClose: onCloseModal,
}) => {
  const [t] = useTranslation();
  const { envs, botType } = useProStudioCtxSelector((ctx) => ({
    envs: ctx.envs || [],
    botType: ctx.selectedBot?.botType,
  }));

  const initialEnv = 'sandbox';
  const [selectedEnv, setSelectedEnv] = useState<string>(initialEnv);
  const [isLoading, setIsLoading] = useState(false);

  const onClose = () => {
    onCloseModal?.();
    setSelectedEnv(initialEnv);
  };

  const onPublish = () => {
    const version = botVersion?.version;
    if (!selectedEnv || !version || !botType) {
      return;
    }

    setIsLoading(true);

    sendProStudioEvent({
      type: 'publishBot',
      payload: {
        republish: true,
        env: selectedEnv,
        version,
        botType,
        onPublishComplete: (result) => {
          onClose();
          setIsLoading(false);
          if (result.status === 'error') {
            // Only a rejected mutation is reported here. Once the mutation is accepted, the
            // republish is a deployment row like any other, and its progress and outcome are
            // announced by usePublishStatusWatcherForCoworkers — identically for everyone
            // looking at the solution.
            sendUserMessage({
              type: 'error',
              text:
                t('pro-studio/navigation:publish_modal.toast.publish_failed', {
                  defaultValue:
                    'There was an error publishing this solution. Please reach out to your Pypestream point of contact for assistance',
                }) ?? '',
              autoClose: 5000,
            });
          }
        },
      },
    });
  };

  return (
    <GenericModal
      test-id="republish-modal"
      modalRef={modalRef}
      onClose={onClose}
      onError={() => setIsLoading(false)}
    >
      <TextTitle size="small">
        <TranslationComponent i18nKey="pro-studio/versions:republish_modal.title">
          Republish
        </TranslationComponent>{' '}
        <span color="primary">{botVersion?.version}</span>
      </TextTitle>
      <Spacer size="small" />
      <TextSubtitle size="small">
        <TranslationComponent i18nKey="pro-studio/versions:republish_modal.subtitle">
          Select an environment:
        </TranslationComponent>
      </TextSubtitle>
      <Spacer size="small" />
      <RadioGroup
        value={selectedEnv}
        orientation="horizontal"
        size="small"
        test-id="ps-republish-version-modal-select-env"
        onChange={(e) => setSelectedEnv(e.target.value as string)}
      >
        {[...envs].reverse().map((env) => (
          <Radio key={env} value={env} disabled={isLoading}>
            {capitalizeWords(env)}
          </Radio>
        ))}
      </RadioGroup>
      {/* <Select
        test-id="ps-republish-version-modal-select-env"
        placeholder={
          t('pro-studio/versions:republish_modal.select.placeholder', {
            defaultValue: 'Select environment',
          }) || ''
        }
        variant="outlined"
        value={selectedEnv}
        onChange={(e) => setSelectedEnv(e.target.value as string)}
        disabled={isLoading}
      >
        {envs?.map((env) => (
          <SelectOption key={env} value={env}>
            {capitalizeWords(env)}
          </SelectOption>
        ))}
      </Select> */}
      {/* <Spacer size="large" />
      <RadioGroup size="large">
        {radioOptions.map((option) => (
          <Radio key={option.value} value={option.value}>
            <TranslationComponent
              i18nKey={
                `pro-studio/versions:republish_modal.radio_group.radio${option.value}` as TranslationKeys
              }
            >
              {option.label}
            </TranslationComponent>
          </Radio>
        ))}
      </RadioGroup> */}
      <Button
        test-id="ps-republish-version-modal-republish-btn"
        size="large"
        slot="footer"
        onClick={onPublish}
        disabled={!selectedEnv || isLoading}
      >
        <TranslationComponent
          i18nKey={
            `pro-studio/versions:republish_modal.button.${
              isLoading ? 'loading' : 'static'
            }` as TranslationKeys
          }
        >
          {isLoading ? 'Republishing...' : 'Republish'}
        </TranslationComponent>
      </Button>
    </GenericModal>
  );
};
